/**
 * MDM / ICD catalog tree fixture for add-library search until API is used for all flows.
 * Shape aligns with ICD code table columns (ID, NormalizedCode, ICDType, Level, billable flags, effective dates, etc.).
 * Consumed by LabadminService.
 */

export interface IcdCodeLibraryApiTreeNode {
  MDMICDID: number | null;
  ICDCode: string;
  NormalizedCode: string;
  ICDCodeName: string;
  ICDCodeDescription: string | null;
  ICDType: string;
  CodeSystem: string;
  VersionYear: number;
  Category: string | null;
  ParentId: number | null;
  Level: number;
  IsBillable: boolean;
  IsActive: boolean;
  EffectiveFrom: string;
  EffectiveTo: string | null;
  AliasName?: string;
  ICDcodeVersion?: string;
  FullPath?: string;
  SearchKeywords?: string;
  children?: IcdCodeLibraryApiTreeNode[];
}

export const ICD_CODE_LIBRARY_DUMMY_ROOTS: IcdCodeLibraryApiTreeNode[] = [
  {
    MDMICDID: 1,
    ICDCode: 'A00',
    NormalizedCode: 'A00',
    ICDCodeName: 'Cholera',
    ICDCodeDescription: 'Cholera infection',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Infectious Diseases',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Cholera Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 2,
        ICDCode: 'A00.0',
        NormalizedCode: 'A000',
        ICDCodeName: 'Cholera due to Vibrio cholerae 01',
        ICDCodeDescription: 'Severe cholera infection',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Infectious Diseases',
        ParentId: 1,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Cholera Type 1',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 7,
    ICDCode: 'A01',
    NormalizedCode: 'A01',
    ICDCodeName: 'Typhoid',
    ICDCodeDescription: 'Typhoid fever',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Infectious Diseases',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Typhoid Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 8,
        ICDCode: 'A01.0',
        NormalizedCode: 'A010',
        ICDCodeName: 'Typhoid fever',
        ICDCodeDescription: 'Bacterial infection',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Infectious Diseases',
        ParentId: 7,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Typhoid Basic',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 11,
    ICDCode: 'E11',
    NormalizedCode: 'E11',
    ICDCodeName: 'Type 2 Diabetes Mellitus',
    ICDCodeDescription: 'Chronic condition affecting sugar metabolism',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Endocrine',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Diabetes Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 12,
        ICDCode: 'E11.9',
        NormalizedCode: 'E119',
        ICDCodeName: 'Diabetes without complications',
        ICDCodeDescription: 'Stable diabetes',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Endocrine',
        ParentId: 11,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Simple Diabetes',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 13,
        ICDCode: 'E11.65',
        NormalizedCode: 'E1165',
        ICDCodeName: 'Diabetes with hyperglycemia',
        ICDCodeDescription: 'High blood sugar',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Complication',
        ParentId: 11,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'High Sugar',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 20,
    ICDCode: 'I10',
    NormalizedCode: 'I10',
    ICDCodeName: 'Essential Hypertension',
    ICDCodeDescription: 'High blood pressure',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Cardiovascular',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'BP Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 21,
        ICDCode: 'I10.1',
        NormalizedCode: 'I101',
        ICDCodeName: 'Hypertension with heart disease',
        ICDCodeDescription: 'BP affecting heart',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Complication',
        ParentId: 20,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'BP Heart',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 22,
        ICDCode: 'I10.2',
        NormalizedCode: 'I102',
        ICDCodeName: 'Hypertension with kidney disease',
        ICDCodeDescription: 'BP affecting kidney',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Complication',
        ParentId: 20,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'BP Kidney',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 30,
    ICDCode: 'J45',
    NormalizedCode: 'J45',
    ICDCodeName: 'Asthma',
    ICDCodeDescription: 'Chronic airway inflammation',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Respiratory',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Asthma Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 31,
        ICDCode: 'J45.0',
        NormalizedCode: 'J450',
        ICDCodeName: 'Allergic asthma',
        ICDCodeDescription: 'Triggered by allergens',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Respiratory',
        ParentId: 30,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Allergic Asthma',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 32,
        ICDCode: 'J45.1',
        NormalizedCode: 'J451',
        ICDCodeName: 'Non-allergic asthma',
        ICDCodeDescription: 'Non-allergic triggers',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Respiratory',
        ParentId: 30,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Non-Allergic Asthma',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 33,
        ICDCode: 'J45.9',
        NormalizedCode: 'J459',
        ICDCodeName: 'Unspecified asthma',
        ICDCodeDescription: 'General asthma condition',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Respiratory',
        ParentId: 30,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'General Asthma',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 40,
    ICDCode: 'F32',
    NormalizedCode: 'F32',
    ICDCodeName: 'Major depressive disorder, single episode',
    ICDCodeDescription: 'Depressive episode',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Mental Health',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Depression Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 41,
        ICDCode: 'F32.0',
        NormalizedCode: 'F320',
        ICDCodeName: 'Mild depressive episode',
        ICDCodeDescription: 'Mild depression symptoms',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Mental Health',
        ParentId: 40,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Mild Depression',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 42,
        ICDCode: 'F32.1',
        NormalizedCode: 'F321',
        ICDCodeName: 'Moderate depressive episode',
        ICDCodeDescription: 'Moderate depression symptoms',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Mental Health',
        ParentId: 40,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Moderate Depression',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 43,
        ICDCode: 'F32.2',
        NormalizedCode: 'F322',
        ICDCodeName: 'Severe depressive episode',
        ICDCodeDescription: 'Severe depression symptoms',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Mental Health',
        ParentId: 40,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Severe Depression',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 50,
    ICDCode: 'M54',
    NormalizedCode: 'M54',
    ICDCodeName: 'Dorsalgia',
    ICDCodeDescription: 'Back pain conditions',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Musculoskeletal',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Back Pain Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 51,
        ICDCode: 'M54.5',
        NormalizedCode: 'M545',
        ICDCodeName: 'Low back pain',
        ICDCodeDescription: 'Lumbar region pain',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Musculoskeletal',
        ParentId: 50,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Lower Back Pain',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 52,
        ICDCode: 'M54.2',
        NormalizedCode: 'M542',
        ICDCodeName: 'Cervicalgia',
        ICDCodeDescription: 'Neck pain',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Musculoskeletal',
        ParentId: 50,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Neck Pain',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 60,
    ICDCode: 'K21',
    NormalizedCode: 'K21',
    ICDCodeName: 'Gastro-esophageal reflux disease',
    ICDCodeDescription: 'Acid reflux condition',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Digestive',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'GERD Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 61,
        ICDCode: 'K21.0',
        NormalizedCode: 'K210',
        ICDCodeName: 'GERD with esophagitis',
        ICDCodeDescription: 'Inflammation present',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Digestive',
        ParentId: 60,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'GERD Severe',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      { 
        MDMICDID: 62,
        ICDCode: 'K21.9',
        NormalizedCode: 'K219',
        ICDCodeName: 'GERD without esophagitis',
        ICDCodeDescription: 'No inflammation',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Digestive',
        ParentId: 60,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'GERD Mild',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 70,
    ICDCode: 'S72',
    NormalizedCode: 'S72',
    ICDCodeName: 'Fracture of femur',
    ICDCodeDescription: 'Thigh bone fracture',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Injury',
    ParentId: null,
    Level: 0,
    IsBillable: false,
    IsActive: true,
    EffectiveFrom: '2015-10-01',
    EffectiveTo: null,
    AliasName: 'Femur Fracture Root',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: [
      {
        MDMICDID: 71,
        ICDCode: 'S72.0',
        NormalizedCode: 'S720',
        ICDCodeName: 'Fracture of neck of femur',
        ICDCodeDescription: 'Hip fracture',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Injury',
        ParentId: 70,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Hip Fracture',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 72,
        ICDCode: 'S72.1',
        NormalizedCode: 'S721',
        ICDCodeName: 'Pertrochanteric fracture',
        ICDCodeDescription: 'Upper femur fracture',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Injury',
        ParentId: 70,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Upper Femur',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      },
      {
        MDMICDID: 73,
        ICDCode: 'S72.2',
        NormalizedCode: 'S722',
        ICDCodeName: 'Subtrochanteric fracture',
        ICDCodeDescription: 'Lower femur fracture',
        ICDType: 'ICD-10-CM',
        CodeSystem: 'ICD-10',
        VersionYear: 2024,
        Category: 'Injury',
        ParentId: 70,
        Level: 1,
        IsBillable: true,
        IsActive: true,
        EffectiveFrom: '2015-10-01',
        EffectiveTo: null,
        AliasName: 'Lower Femur',
        ICDcodeVersion: 'ICD-10',
        FullPath: '',
        SearchKeywords: '',
        children: []
      }
    ]
  },
  {
    MDMICDID: 74,
    ICDCode: 'A11.0',
    NormalizedCode: 'A110',
    ICDCodeName: 'Typhoid fever test',
    ICDCodeDescription: 'Bacterial infection Test',
    ICDType: 'ICD-10-CM',
    CodeSystem: 'ICD-10',
    VersionYear: 2024,
    Category: 'Infectious Diseases Test',
    ParentId: null,
    Level: 0,
    IsBillable: true,
    IsActive: true,
    EffectiveFrom: '2015-10-02',
    EffectiveTo: null,
    AliasName: 'Typhoid Basic',
    ICDcodeVersion: 'ICD-10',
    FullPath: '',
    SearchKeywords: '',
    children: []
  }
];
